import React, { useState, useEffect } from 'react';
import { useProject } from '../../../contexts/ProjectContext';

const ProjectPhotosModal = ({ isOpen, onClose }) => {
  const { project } = useProject();
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Fotos del proyecto desde el contexto (URLs de Cloudinary)
  const projectImages = project?.images || [];

  // Convertir URLs de Cloudinary a objetos de foto (solo del API)
  const titles = [
    'Vista aérea del proyecto',
    'Infraestructura y servicios',
    'Zonificación del área',
    'Amenidades y áreas verdes',
    'Acceso principal'
  ];

  const descriptions = [
    'Perspectiva general del desarrollo inmobiliario',
    'Servicios públicos y vialidades principales',
    'Distribución de usos de suelo y lotes',
    'Espacios recreativos y zonas comunes',
    'Entrada principal y seguridad del proyecto'
  ];

  const photos = projectImages.map((url, index) => ({
    id: index + 1,
    url: url,
    title: titles[index] || `Imagen ${index + 1}`,
    description: descriptions[index] || `Foto del proyecto - Imagen ${index + 1}`
  }));

  const selectedPhoto = photos[selectedIndex] || null;

  // Resetear al índice 0 cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Si no hay fotos, mostrar mensaje
  if (photos.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <div className="text-center">
            <div className="text-6xl mb-4">📷</div>
            <h2 className="text-xl font-professional-semibold text-gray-800 mb-2">
              Sin Fotos Disponibles
            </h2>
            <p className="text-sm font-professional-light text-gray-600 mb-6">
              No hay fotos disponibles para este proyecto en este momento.
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors font-professional-semibold"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">
              Fotos del proyecto
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex h-[65vh]">
          {/* Foto principal */}
          <div className="flex-1 p-4">
            <div className="h-full bg-gray-100 rounded-lg overflow-hidden">
              {selectedPhoto && (
                <img
                  src={selectedPhoto.url || selectedPhoto.src}
                  alt={selectedPhoto.title || selectedPhoto.alt || 'Foto del proyecto'}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          </div>

          {/* Galería lateral */}
          <div className="w-40 border-l border-gray-200 p-3 overflow-y-auto">
            <h4 className="text-xs font-semibold text-gray-800 mb-3">
              Galería ({photos.length})
            </h4>
            <div className="space-y-2">
              {photos.map((photo, index) => (
                <button
                  key={photo.id || index}
                  onClick={() => setSelectedIndex(index)}
                  className={`w-full aspect-square rounded-lg overflow-hidden transition-all ${
                    selectedIndex === index
                      ? 'ring-2 ring-brand-500 ring-offset-1'
                      : 'hover:opacity-80'
                  }`}
                >
                  <img
                    src={photo.url || photo.src}
                    alt={photo.title || photo.alt || `Foto ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-600">
              {photos.length > 0 ? (selectedIndex + 1) : 0} de {photos.length} fotos
            </div>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors font-semibold"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectPhotosModal;
